import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { HashRouter as Router, Routes, Route } from 'react-router-dom'
import type { AppState, AccessCode } from './types'
import { DEFAULT_STATE } from './defaults'
import { calculerResultats, calcRevente } from './calculs'
import { exportTableauDeBord } from './lib/exportXlsx'
import { loadSalonData, saveSalonData, supabase } from './supabase'
import AccessScreen from './components/AccessScreen'
import AdminLogin from './components/AdminLogin'
import AdminDashboard from './components/AdminDashboard'
import Header from './components/Header'
import MonComptePanel from './components/MonComptePanel'
import Footer from './components/Footer'
import SectionInfosSalon from './components/SectionInfosSalon'
import SectionCharges from './components/SectionCharges'
import SectionCollaborateurs from './components/SectionCollaborateurs'
import SectionGerant from './components/SectionGerant'
import SectionRevente, { type ReventeData } from './components/SectionRevente'
import SectionParametres from './components/SectionParametres'
import ResultatsSeuil from './components/ResultatsSeuil'

const AUTH_KEY = 'demoSeuilAuth'

// ── Modal de confirmation réinitialisation ─────────────────────────────────
function ConfirmResetModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="text-center mb-4">
          <div className="text-3xl mb-2">🔄</div>
          <h2 className="text-base font-bold text-gray-800">Réinitialiser les données ?</h2>
          <p className="text-sm text-gray-500 mt-1">Toutes les données de ce salon seront effacées. Cette action est irréversible.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button onClick={onConfirm}
            className="flex-1 px-4 py-2 text-sm font-medium rounded-xl text-white transition-colors"
            style={{background:'#C62828'}}>
            Réinitialiser
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Bandeau profil salon ───────────────────────────────────────────────────
function SalonProfileBadge({ code, salonInfo }: { code: string; salonInfo: AccessCode | null }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
      <div className="flex items-center gap-2">
        <span className="text-blue-500 text-base">💈</span>
        <div>
          {salonInfo ? (
            <>
              <div className="text-sm font-bold text-blue-900">{salonInfo.salon}</div>
              <div className="text-xs text-blue-500">{salonInfo.prenom} {salonInfo.nom}</div>
            </>
          ) : (
            <div className="text-sm font-semibold text-blue-700">Votre espace salon</div>
          )}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-blue-100">
        <span className="text-gray-400 text-xs">🔑</span>
        <span className="text-xs font-mono font-semibold text-blue-800">{code}</span>
      </div>
    </div>
  )
}

function CalculatorPage({ authCode, onLogout }: { authCode?: string; onLogout: () => void }) {
  // Toujours démarrer vide — les vraies données sont chargées depuis Supabase/localStorage
  const [state, setState] = useState<AppState>(() => {
    try {
      const key = `salon_${authCode ?? 'default'}`
      const saved = localStorage.getItem(key)
      if (saved) {
        const parsed = JSON.parse(saved) as AppState
        // Migration : ajouter le champ gerant s'il n'existe pas dans les anciennes données
        if (!parsed.gerant) {
          parsed.gerant = { coutTotal: 0, heuresMois: 0, tauxEfficacite: 100 }
        }
        // Migration : convertir statut 'Dirigeant' → 'Collaborateur'
        if (parsed.collaborateurs) {
          parsed.collaborateurs = parsed.collaborateurs.map(c =>
            (c.statut as string) === 'Dirigeant'
              ? { ...c, statut: 'Collaborateur' as const }
              : c
          )
        }
        return parsed
      }
    } catch { /* ignore */ }
    // Copie profonde de l'état vide pour éviter les mutations
    return JSON.parse(JSON.stringify(DEFAULT_STATE)) as AppState
  })
  const [reventeData, setReventeData] = useState<ReventeData>({
    ventesAddTTC: 0, coeffRevente: 2.2, tauxTVA: 20,
    commissionPct: 10, tauxChargesSociales: 45
  })
  const [caReel, setCaReel] = useState(0)
  const [showResetModal, setShowResetModal] = useState(false)
  const [showMonCompte, setShowMonCompte] = useState(false)
  const [salonInfo, setSalonInfo] = useState<AccessCode | null>(null)

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Charger depuis Supabase au montage
  useEffect(() => {
    if (!authCode) return
    loadSalonData(authCode).then(remoteState => {
      if (remoteState) {
        // ── Migration données existantes ─────────────────────────────────────
        // 1. Ajouter le champ gerant s'il n'existe pas (avant l'ajout de la section dédiée)
        if (!remoteState.gerant) {
          remoteState.gerant = { coutTotal: 0, heuresMois: 0, tauxEfficacite: 100 }
        }
        // 2. Retirer les collabs avec statut 'Dirigeant' (maintenant géré dans SectionGerant)
        //    → on les convertit en 'Collaborateur' pour ne pas perdre les données salariales
        if (remoteState.collaborateurs) {
          remoteState.collaborateurs = remoteState.collaborateurs.map(c =>
            (c.statut as string) === 'Dirigeant'
              ? { ...c, statut: 'Collaborateur' as const }
              : c
          )
        }
        setState(remoteState)
      }
    })
    // Charger les infos du salon (nom, gérant) depuis access_codes
    void supabase
      .from('access_codes')
      .select('*')
      .eq('code', authCode.toUpperCase())
      .single()
      .then(({ data }) => {
        if (data) setSalonInfo(data as AccessCode)
      })
  }, [authCode])

  const resultats = useMemo(
    () => calculerResultats(state.charges, state.collaborateurs, state.gerant, state.parametres),
    [state.charges, state.collaborateurs, state.gerant, state.parametres]
  )

  // Cumul des ventes TTC de tous les collaborateurs → report dans SectionRevente
  const totalReventeCollabsTTC = useMemo(
    () => state.collaborateurs.reduce((s, c) => s + (c.venteMoisTTC ?? 0), 0),
    [state.collaborateurs]
  )

  const configStep = useMemo(() => {
    let s = 0
    if (resultats.chargesFixesMensuelles > 0) s++
    if (state.collaborateurs.length > 0) s++
    if (resultats.heuresProductivesTotales > 0) s++
    return s
  }, [resultats, state.collaborateurs.length])

  const handleStateChange = useCallback((update: Partial<AppState>) => {
    setState((prev: AppState) => {
      const next = { ...prev, ...update }
      const key = `salon_${authCode ?? 'default'}`
      try { localStorage.setItem(key, JSON.stringify(next)) } catch { /* noop */ }
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        if (authCode) saveSalonData(authCode, next)
      }, 2000)
      return next
    })
  }, [authCode])

  const doReset = useCallback(() => {
    const key = `salon_${authCode ?? 'default'}`
    // Copie profonde pour éviter toute référence partagée avec DEFAULT_STATE
    const emptyState: AppState = JSON.parse(JSON.stringify(DEFAULT_STATE))
    localStorage.removeItem(key)
    setState(emptyState)
    setCaReel(0)
    setReventeData({ ventesAddTTC: 0, coeffRevente: 2.2, tauxTVA: 20, commissionPct: 10, tauxChargesSociales: 45 })
    if (authCode) saveSalonData(authCode, emptyState)
    setShowResetModal(false)
  }, [authCode])

  const exportXlsx = useCallback(() => {
    exportTableauDeBord(state, resultats, caReel)
  }, [state, resultats, caReel])

  return (
    <div className="min-h-screen bg-gray-50">
      {showResetModal && (
        <ConfirmResetModal
          onConfirm={doReset}
          onCancel={() => setShowResetModal(false)}
        />
      )}

      <MonComptePanel
        open={showMonCompte}
        onClose={() => setShowMonCompte(false)}
        onLogout={onLogout}
        authCode={authCode ?? ''}
        salonInfo={salonInfo}
      />
      <Header
        onLogout={onLogout}
        onOpenCompte={() => setShowMonCompte(true)}
        salonInfo={salonInfo}
      />

      <main className="max-w-7xl mx-auto px-4 py-6">

        <div className="flex gap-6 items-start">
          {/* ── Colonne gauche : formulaires ── */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="flex items-center justify-between">
              <h1 className="text-base font-bold text-gray-800">Calculateur Seuil de Rentabilité</h1>
              <div className="flex gap-2">
                <button onClick={exportXlsx}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-gray-500 hover:text-gray-700 transition-colors">
                  📥 Exporter Excel
                </button>
                <button onClick={() => setShowResetModal(true)}
                  className="px-3 py-1.5 text-xs font-medium border border-red-200 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                  🔄 Réinitialiser
                </button>
              </div>
            </div>

            <SectionParametres
              parametres={state.parametres}
              onChange={parametres => handleStateChange({ parametres })}
              configStep={configStep}
            />
            <SectionInfosSalon
              salon={state.salon}
              onChange={salon => handleStateChange({ salon })}
            />
            <SectionCharges
              charges={state.charges}
              onChange={charges => handleStateChange({ charges })}
            />
            <SectionGerant
              gerant={state.gerant}
              onChange={gerant => handleStateChange({ gerant })}
            />
            <SectionCollaborateurs
              collaborateurs={state.collaborateurs}
              onChange={collaborateurs => handleStateChange({ collaborateurs })}
              seuilsIndividuels={resultats.seuilsIndividuels}
            />
            <SectionRevente data={reventeData} onChange={setReventeData} reventeCollabsTTC={totalReventeCollabsTTC} />
          </div>

          {/* ── Colonne droite : résultats sticky (desktop) ── */}
          <div className="hidden md:block w-80 xl:w-96 flex-shrink-0">
            <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto pb-4 space-y-0">
              <ResultatsSeuil
                resultats={resultats}
                collaborateurs={state.collaborateurs}
                parametres={state.parametres}
                caReel={caReel}
                onCaReelChange={setCaReel}
                onModeGerantChange={mode => handleStateChange({ parametres: { ...state.parametres, modeGerant: mode } })}
              />
            </div>
          </div>
        </div>

        {/* ── Mobile : résultats en bas ── */}
        <div className="md:hidden mt-6">
          <ResultatsSeuil
            resultats={resultats}
            collaborateurs={state.collaborateurs}
            parametres={state.parametres}
            caReel={caReel}
            onCaReelChange={setCaReel}
            onModeGerantChange={mode => handleStateChange({ parametres: { ...state.parametres, modeGerant: mode } })}
          />
        </div>
      </main>

      <Footer />
    </div>
  )
}

function CalculatorGate() {
  const [authenticated, setAuthenticated] = useState(() => !!localStorage.getItem(AUTH_KEY))
  const [authCode, setAuthCode] = useState<string | undefined>(() => {
    const raw = localStorage.getItem(AUTH_KEY)
    return raw ?? undefined
  })

  const handleLogout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY)
    setAuthenticated(false)
    setAuthCode(undefined)
  }, [])

  if (!authenticated) {
    return (
      <AccessScreen
        onAuthenticated={(code: string) => {
          localStorage.setItem(AUTH_KEY, code)
          setAuthCode(code)
          setAuthenticated(true)
        }}
      />
    )
  }

  return <CalculatorPage authCode={authCode} onLogout={handleLogout} />
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CalculatorGate />} />
        <Route path="/admin" element={<AdminLogin onAuthenticated={() => {}} />} />
        <Route path="/admin/dashboard" element={<AdminDashboard onLogout={() => {}} />} />
      </Routes>
    </Router>
  )
}
